import React, { useState, useEffect, useContext, lazy, Suspense } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles, Paper, Tabs, Tab } from "@material-ui/core";
import TabPanel from "../../components/TabPanel";
import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";
import OnlyForSuperUser from "../../components/OnlyForSuperUser";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage/index.js";

const SchedulesForm = lazy(() => import("../../components/SchedulesForm"));
const CompaniesManager = lazy(() => import("../../components/CompaniesManager"));
const PlansManager = lazy(() => import("../../components/PlansManager"));
const HelpsManager = lazy(() => import("../../components/HelpsManager"));
const Options = lazy(() => import("../../components/Settings/Options"));
const Whitelabel = lazy(() => import("../../components/Settings/Whitelabel"));
const BackupRestore = lazy(() => import("../../components/Settings/BackupRestore"));
const SmtpSettings = lazy(() => import("../../components/Settings/SmtpSettings"));
const FinalizacaoAtendimento = lazy(() => import("../../components/Settings/FinalizacaoAtendimento"));

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
  },
  tab: {
    // background: "#f2f5f3",
    backgroundColor: theme.mode === "light" ? "#f2f2f2" : "#1a1c2e",
    borderRadius: 4,
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  //novo hook
  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      if (!user || !user.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        // Fetch all data in parallel for faster loading
        const [company, settingList, settingListOld] = await Promise.all([
          find(companyId),
          getAllSettings(companyId),
          getAllSettingsOld(),
        ]);

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);

        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;
    const onSettingsEvent = () => {
      getAllSettingsOld().then(setOldSettings);
    };
    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success(i18n.t("hardcoded.schedulesUpdated"));
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => {
    return currentUser && currentUser.super;
  };

  return (
    <MainContainer className={classes.root}>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>{i18n.t("settings.title")}</Title>
          </MainHeader>
          <Paper className={classes.mainPaper} elevation={1}>
            <Tabs
              value={tab}
              indicatorColor="primary"
              textColor="primary"
              scrollButtons="on"
              variant="scrollable"
              onChange={handleTabChange}
              className={classes.tab}
            >
              <Tab label={i18n.t("settings.tabs.options")} value={"options"} />
              {schedulesEnabled && <Tab label="Horários" value={"schedules"} />}
              {user.profile === "admin" &&
                user.finalizacaoComValorVendaAtiva && (
                  <Tab
                    label="Finalização do Atendimento"
                    value={"finalizacao"}
                  />
                )}
              {isSuper() ? (
                <Tab
                  label={i18n.t("settings.tabs.companies")}
                  value={"companies"}
                />
              ) : null}
              {isSuper() ? (
                <Tab label={i18n.t("settings.tabs.plans")} value={"plans"} />
              ) : null}
              {isSuper() ? (
                <Tab label={i18n.t("settings.tabs.helps")} value={"helps"} />
              ) : null}
              {isSuper() ? (
                <Tab label="Whitelabel" value={"whitelabel"} />
              ) : null}
              {isSuper() ? (
                <Tab label="Backup" value={"backup"} />
              ) : null}
              {isSuper() ? (
                <Tab label="SMTP" value={"smtp"} />
              ) : null}
            </Tabs>
            <Paper className={classes.paper} elevation={0}>
              <Suspense fallback={<div style={{ padding: 20, textAlign: "center" }}>Carregando...</div>}>
                {tab === "schedules" && (
                  <div className={classes.container}>
                    <SchedulesForm
                      loading={loading}
                      onSubmit={handleSubmitSchedules}
                      initialValues={schedules}
                    />
                  </div>
                )}
                {tab === "companies" && isSuper() && (
                  <div className={classes.container}><CompaniesManager /></div>
                )}
                {tab === "plans" && isSuper() && (
                  <div className={classes.container}><PlansManager /></div>
                )}
                {tab === "helps" && isSuper() && (
                  <div className={classes.container}><HelpsManager /></div>
                )}
                {tab === "whitelabel" && isSuper() && (
                  <div className={classes.container}><Whitelabel settings={oldSettings} /></div>
                )}
                {tab === "backup" && isSuper() && (
                  <div className={classes.container}><BackupRestore /></div>
                )}
                {tab === "smtp" && isSuper() && (
                  <div className={classes.container}><SmtpSettings /></div>
                )}
                {tab === "finalizacao" && (
                  <div className={classes.container}>
                    <FinalizacaoAtendimento
                      settings={settings}
                      onSettingsChange={(newSettings) => setSettings(newSettings)}
                    />
                  </div>
                )}
                {tab === "options" && (
                  <div className={classes.container}>
                    <Options
                      settings={settings}
                      oldSettings={oldSettings}
                      user={currentUser}
                      scheduleTypeChanged={(value) =>
                        setSchedulesEnabled(value === "company")
                      }
                    />
                  </div>
                )}
              </Suspense>
            </Paper>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default SettingsCustom;
